/**
 * Dynamic onboarding step loader.
 *
 * Reads `:stepId` from URL params, looks up the component in the
 * step registry, and renders it inside a Suspense boundary. A step outside
 * the tenant's plan (an old bookmark, a hand-typed URL) redirects to the
 * step the tenant is actually on, because the backend would refuse it.
 */
import { Suspense } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, OnboardingProgress } from '@lipaflow/shared'
import { STEP_COMPONENTS } from './registry'
import { onboardingStepPath, useOnboardingPlan } from '../../lib/onboardingNavigation'

function StepSpinner() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
    </div>
  )
}

export default function StepLoader() {
  const { stepId } = useParams<{ stepId: string }>()
  const { plan, isReady } = useOnboardingPlan()
  const { data: progress } = useQuery<OnboardingProgress>({
    queryKey: ['onboarding-progress'],
    queryFn: async () => (await api.get('/onboarding/progress')).data,
  })

  if (!stepId || !STEP_COMPONENTS[stepId]) {
    return <Navigate to="/onboarding/step/set_institution_details" replace />
  }

  // Wait for the plan and progress: rendering first would mount an off-plan
  // page, with its own queries, before the redirect.
  if (!isReady || !progress) {
    return <StepSpinner />
  }

  if (!plan.includes(stepId)) {
    // Go to the tenant's current step, unless that is this very page (the
    // backend and the plan disagree); then start the plan from the top.
    const current = onboardingStepPath(progress.current_step)
    const destination = current === onboardingStepPath(stepId) ? onboardingStepPath(plan[0]) : current
    return <Navigate to={destination} replace />
  }

  const StepComponent = STEP_COMPONENTS[stepId]

  return (
    <Suspense fallback={<StepSpinner />}>
      <StepComponent />
    </Suspense>
  )
}
